<script setup>
import { computed, onMounted, ref } from 'vue';

import { ChatFooter, ChatHeader, HelpPrompt, MessagesList } from '@chat/components';
import { useStore } from '@/store';
import { useI18n } from '@/locales';
import { useToast } from '@base/hooks';

defineEmits(['close']);

defineProps({
  helpLink: {
    type: String,
    default: 'https://ajuda.clicksign.com/automação-de-documentos',
  },
  prompts: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const { t } = useI18n();
const toast = useToast();

const maximized = ref(false);
const loadingMessage = ref(false);

const messages = computed(() => store.getters['chat/getMessages']);

const resizeAction = () => {
  maximized.value = !maximized.value;
};

const sendMessage = async (message) => {
  loadingMessage.value = true;
  await store.dispatch('chat/sendMessage', {
    message,
  });
  loadingMessage.value = false;
};

onMounted(async () => {
  try {
    await store.dispatch('chat/initChatSession');
  } catch (err) {
    toast.error(t('chatMessage.chatSessionError'));
  }
});
</script>

<template>
  <XCard
    :class="[$style.container, maximized && $style.maximized]"
    elevation="high"
  >
    <ChatHeader
      @resize="resizeAction"
      @close="$emit('close')"
    />
    <div :class="$style.body">
      <HelpPrompt
        v-if="messages.length === 0"
        :prompts="prompts"
        @send-message="sendMessage"
      />
      <MessagesList
        v-else
        :loading="loadingMessage"
        :messages="messages"
      />
    </div>
    <ChatFooter
      :help-link="helpLink"
      @send-message="sendMessage"
    />
  </XCard>
</template>

<style lang="scss" module>
.container {
  position: fixed;
  right: var(--space-regular);
  bottom: var(--space-regular);
  display: flex;
  flex-direction: column;
  width: 376px;
  height: 393px;
  gap: var(--space-regular);

  &.maximized {
    height: 80%;
  }

  .body {
    flex: 1;
    overflow-y: auto;
  }
}
</style>
